import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import useAuthStore from '../store/authStore';
import { Factory, Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';

export default function Login() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { document.documentElement.dir = i18n.language === 'fa' ? 'rtl' : 'ltr'; }, [i18n.language]);

  useEffect(() => {
    const saved = localStorage.getItem('saved_username');
    if (saved) setForm(f => ({ ...f, username: saved }));
  }, []);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
    document.dir = lang === 'fa' ? 'rtl' : 'ltr';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const r = await authAPI.login({ username: form.username, password: form.password });
      const token = r.data.access_token;
      localStorage.setItem('token', token);
      const me = await authAPI.me();
      localStorage.setItem('saved_username', form.username);
      setAuth(me.data, token);
      navigate('/');
    } catch {
      setError(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const lang = i18n.language;

  return (
    <div className="login-shell" style={{
      background: '#070b14',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Animated background blobs */}
      <div style={{
        position: 'fixed', top: '-15%', left: '-10%',
        width: 600, height: 600, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)',
        animation: 'float1 9s ease-in-out infinite',
        pointerEvents: 'none', zIndex: 0,
      }} />
      <div style={{
        position: 'fixed', bottom: '-20%', right: '-10%',
        width: 700, height: 700, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(67,56,202,0.14) 0%, transparent 70%)',
        animation: 'float2 13s ease-in-out infinite',
        pointerEvents: 'none', zIndex: 0,
      }} />
      <div style={{
        position: 'fixed', top: '55%', left: '35%',
        width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(139,92,246,0.10) 0%, transparent 70%)',
        animation: 'float3 11s ease-in-out infinite',
        pointerEvents: 'none', zIndex: 0,
      }} />

      <style>{`
        @keyframes float1 {
          0%, 100% { transform: translateY(0px) scale(1); }
          33% { transform: translateY(-30px) scale(1.04); }
          66% { transform: translateY(20px) scale(0.97); }
        }
        @keyframes float2 {
          0%, 100% { transform: translateY(0px) scale(1) rotate(0deg); }
          40% { transform: translateY(-40px) scale(1.06) rotate(2deg); }
          70% { transform: translateY(18px) scale(0.96) rotate(-1deg); }
        }
        @keyframes float3 {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-25px) translateX(20px); }
        }
        @keyframes loginFadeIn {
          from { opacity: 0; transform: translateY(28px) scale(0.97); }
          to   { opacity: 1; transform: none; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .lang-pill:hover { opacity: 0.9; transform: translateY(-1px); }
      `}</style>

      {/* Language switcher */}
      <div style={{
        position: 'fixed', top: 20, right: 24,
        display: 'flex', gap: 6, zIndex: 10,
      }}>
        {['en', 'ru', 'fa'].map(l => (
          <button
            key={l}
            className="lang-pill"
            onClick={() => changeLanguage(l)}
            style={{
              padding: '7px 14px',
              borderRadius: 99,
              fontSize: 11,
              fontWeight: 800,
              cursor: 'pointer',
              letterSpacing: '0.08em',
              transition: 'all 0.18s',
              fontFamily: 'inherit',
              background: lang === l
                ? 'linear-gradient(135deg, #6366f1, #4338ca)'
                : 'rgba(255,255,255,0.06)',
              color: lang === l ? 'white' : 'rgba(255,255,255,0.4)',
              border: lang === l
                ? '1px solid transparent'
                : '1px solid rgba(255,255,255,0.08)',
              boxShadow: lang === l ? '0 4px 16px rgba(99,102,241,0.45)' : 'none',
            }}
          >
            {l.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Login Card */}
      <div style={{
        width: '100%', maxWidth: 420,
        position: 'relative', zIndex: 1,
        animation: 'loginFadeIn 0.5s cubic-bezier(0.34,1.2,0.64,1) both',
      }}>
        {/* Rainbow top border container */}
        <div style={{
          borderRadius: 24,
          padding: 1,
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #ec4899, #f59e0b)',
          boxShadow: '0 0 60px rgba(99,102,241,0.2), 0 40px 100px rgba(0,0,0,0.7)',
        }}>
          <div style={{
            background: '#0c1120',
            borderRadius: 23,
            padding: '44px 40px 40px',
          }}>
            {/* Logo */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 36 }}>
              <div style={{
                width: 72, height: 72, borderRadius: 22,
                background: 'linear-gradient(135deg, #6366f1, #4338ca)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 20,
                boxShadow: '0 8px 32px rgba(99,102,241,0.6), 0 0 60px rgba(99,102,241,0.25)',
                animation: 'pulse-glow 3s ease-in-out infinite',
              }}>
                <Factory size={36} color="white" strokeWidth={1.8} />
              </div>
              <h1 style={{
                fontFamily: 'inherit',
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: '-0.5px',
                marginBottom: 6,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #fff 30%, rgba(180,190,255,0.75))',
                WebkitBackgroundClip: 'text', backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                {lang === 'fa' ? 'سیستم مدیریت کارخانه' : lang === 'ru' ? 'Система управления заводом' : 'Factory Management'}
              </h1>
              <p style={{
                fontSize: 12, fontWeight: 700,
                color: 'rgba(99,102,241,0.7)',
                textTransform: 'uppercase', letterSpacing: '0.14em',
              }}>
                {lang === 'fa' ? 'ایزوگام' : 'Isogam ERP'}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 18 }}>
                <label style={{
                  display: 'block', fontSize: 11, fontWeight: 700,
                  color: 'rgba(160,175,220,0.45)',
                  marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.09em',
                }}>
                  {t('username')}
                </label>
                <input
                  className="input"
                  style={{ fontSize: 14, padding: '13px 16px' }}
                  value={form.username}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  placeholder={lang === 'fa' ? 'نام کاربری' : lang === 'ru' ? 'Имя пользователя' : 'Username'}
                  autoComplete="username"
                  required
                />
              </div>

              <div style={{ marginBottom: 28 }}>
                <label style={{
                  display: 'block', fontSize: 11, fontWeight: 700,
                  color: 'rgba(160,175,220,0.45)',
                  marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.09em',
                }}>
                  {t('password')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="input"
                    style={{ fontSize: 14, padding: '13px 50px 13px 16px' }}
                    type={showPass ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    placeholder={lang === 'fa' ? 'رمز عبور' : lang === 'ru' ? 'Пароль' : 'Password'}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    style={{
                      position: 'absolute', right: 14, top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'rgba(160,175,220,0.4)', display: 'flex',
                      transition: 'color 0.15s', padding: 4,
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = '#a5b4fc'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(160,175,220,0.4)'}
                  >
                    {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {error && (
                <div style={{
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  borderRadius: 12,
                  padding: '12px 16px',
                  marginBottom: 20,
                  fontSize: 13,
                  color: '#f87171',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}>
                  <AlertCircle size={16} style={{ flexShrink: 0 }} />
                  {lang === 'fa' ? 'نام کاربری یا رمز عبور اشتباه است' : lang === 'ru' ? 'Неверное имя пользователя или пароль' : 'Invalid username or password'}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%', padding: '15px',
                  background: loading ? 'rgba(99,102,241,0.5)' : 'linear-gradient(135deg, #6366f1, #4338ca)',
                  color: 'white', border: 'none', borderRadius: 14,
                  fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
                  boxShadow: loading ? 'none' : '0 6px 24px rgba(99,102,241,0.55)',
                  transition: 'all 0.18s', fontFamily: 'inherit',
                  letterSpacing: '-0.2px',
                }}
                onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 36px rgba(99,102,241,0.7)'; } }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = loading ? 'none' : '0 6px 24px rgba(99,102,241,0.55)'; }}
              >
                {loading ? (
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%',
                    border: '2.5px solid rgba(255,255,255,0.25)',
                    borderTopColor: 'white',
                    animation: 'spin 0.7s linear infinite',
                  }} />
                ) : (
                  <><LogIn size={17} /> {lang === 'fa' ? 'ورود به سیستم' : lang === 'ru' ? 'Войти в систему' : 'Sign In'}</>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
